<script setup lang="ts">
import type { WidgetBody } from '@/types/appTypes'

const app = useNuxtApp()
const route = useRoute()
const router = useRouter()
const storeUser = useStoreUser()
const storeIndex = useStoreIndex()
const storeArticleList = useStoreArticleList()

const { isMobile } = useDevice()
const obs = ref(null)

const pageData = {
  topAdSp: null as WidgetBody | null,
  topAdPc: null as WidgetBody | null,
}

const refData = reactive({
  innerFormData: {
    login_id: '',
    password: '',
  },
  isShowPassword: false,
  socialError: null,
})

useHead({
  meta: [
    {
      name: 'robots',
      content: 'noindex',
    },
  ],
})

onBeforeUnmount(() => {
  if (obs.value) {
    obs.value.resetForm()
  }
  storeUser.clearError()
})

const onLogin = async () => {
  const isValid = await obs.value.validate()
  console.log('onLogin', isValid, refData.innerFormData)
  if (isValid) {
    // バリデーションが通っている状態で送信ボタンがクリックされた場合の処理
    await storeUser.login({
      login: refData.innerFormData,
      fromname: route.query.fromname ? route.query.fromname : null,
    })
  }
}

const onRegistration = () => {
  router.push({ name: 'signup' })
}

const onClickSocial = (site) => {
  socialLogin(site)
}

const socialLogin = async (site) => {
  try {
    const res = await app.$OauthApi.oauthRedirect(
      site,
      'login',
    )
    if (res.data.code === 200000) {
      console.log('url: ', res.data.data.url)
      const url = res.data.data.url
      if (url) {
        window.location.href = url
      }
    } else {
      refData.socialError = 'ログインに失敗しました。(socialLogin)'
    }
  } catch (e) {
    throw createError({
      statusCode: e.statusCode,
      statusMessage: 'ログインエラー(socialLogin)',
    })
  }
}

watch(
  () => storeUser.state.errorMessage,
  (val) => {
    if (val) {
      refData.innerFormData.password = ''
    }
  },
)

const initPage = async () => {
  const [_resSite, _resMaster, resList] = await Promise.all([
    storeIndex.fetchSite(),
    storeIndex.fetchMaster(),
    storeArticleList.loadTopLikeBanner(),
  ])

  if (resList.code === 200000) {
    pageData.topAdSp = resList.data
    pageData.topAdPc = resList.data
  }
}
await initPage()
</script>

<template>
  <div class="p-loginPanel">
    <div class="p-loginPanel_inner">
      <ValidationForm
        ref="obs"
        v-slot:default="{ meta }"
      >
        <div class="p-loginPanel_title">
          IMメンバーID（メールアドレス）でログイン
        </div>
        <div class="c-heading_error">
          <template
            v-if="storeUser.state.errorMessage"
          >
            {{ storeUser.state.errorMessage }}
          </template>
        </div>
        <div class="p-loginPanel_inputBlock">
          <div class="p-loginPanel_inputBlock_labelRow">
            <div class="p-loginPanel_inputBlock_labelRow_text">
              IMメンバーID（メールアドレス)
            </div>
          </div>

          <ValidationTextField
            v-model="refData.innerFormData.login_id"
            name="IMメンバーID"
            variant="outlined"
            density="compact"
            rules="required|email"
          />
        </div>
        <div class="p-loginPanel_inputBlock">
          <div class="p-loginPanel_inputBlock_labelRow">
            <div class="p-loginPanel_inputBlock_labelRow_text">
              パスワード
            </div>
          </div>

          <ValidationTextField
            v-model="refData.innerFormData.password"
            name="パスワード"
            variant="outlined"
            density="compact"
            rules="required"
            v-bind:append-inner-icon="refData.isShowPassword ? 'mdi-eye' : 'mdi-eye-off'"
            v-bind:type="refData.isShowPassword? 'text' : 'password'"
            v-on:click:append-inner="refData.isShowPassword = !refData.isShowPassword"
          />
        </div>
        <div
          class="p-loginPanel_action"
        >
          <v-btn
            class="c-btn_formSubmit"
            v-bind:disabled="storeUser.state.loading || !meta.valid"
            color="primary"
            v-on:click="onLogin"
          >
            <v-icon>mdi-login</v-icon>
            ログイン
          </v-btn>
        </div>
        <div
          class="p-loginPanel_forgotLink"
        >
          <nuxt-link
            v-bind:to="{ name: 'password' }"
          >
            パスワードを忘れた方はこちら
          </nuxt-link>
        </div>
      </ValidationForm>
    </div>

    <template v-if="isMobile">
      <div class="c-listAdvertise">
        <div
          v-if="pageData.topAdSp && pageData.topAdSp.body"
          class="c-listAdvertise_advertiseCms c-advertise"
          v-html="pageData.topAdSp.body"
        />
        <!-- <div
          class="c-listAdvertise_advertise c-advertise"
        >
          <adsbygoogle
            ad-slot="9530995886"
            ad-format="auto"
          ></adsbygoogle>
        </div> -->
      </div>
    </template>

    <div class="p-loginPanel_groupLink">
      <div class="p-loginPanel_groupLink_anotherLogin">
        <!-- 現在SNSログインで不具合が発生しています。<br>
        今しばらくお待ちください。 -->
        <div v-show="1">
          <div class="p-loginPanel_groupLink_anotherLogin_title">
            ソーシャルIDでログイン
          </div>
          <div class="p-form_notice">
            <template v-if="refData.socialError">
              {{ refData.socialError }}
            </template>
          </div>
          <button
            class="p-loginPanel_groupLink_anotherLogin_linkButton"
            v-on:click="onClickSocial('facebook')"
          >
            <img
              src="@/assets/images/login-fb-icon.png"
              class="p-loginPanel_groupLink_anotherLogin_linkButton_iconFaceBook"
            >
            Facebookでログイン
          </button>
          <button
            class="p-loginPanel_groupLink_anotherLogin_linkButton"
            v-on:click="onClickSocial('line')"
          >
            <img
              src="@/assets/images/logo-social-line.png"
              class="p-loginPanel_groupLink_anotherLogin_linkButton_iconLine"
            >
            Lineでログイン
          </button>
        </div>
      </div>
    </div>

    <div class="p-loginPanel_action">
      <v-btn
        class="c-btn_formSubmit c-btn_formSubmit_margin"
        color="primary"
        v-on:click="onRegistration"
      >
        <v-icon>mdi-file-document-edit-outline</v-icon>
        新規登録
      </v-btn>
    </div>

    <div class="p-loginPanel_confirmBox">
      <div class="p-loginPanel_confirmBox_title">
        IMメンバーになると、お得な特典がいっぱい！
      </div>
      <v-divider class="p-loginPanel_confirmBox_divider" />
      <div class="p-loginPanel_confirmBox_infoGroup">
        <table>
          <tbody>
            <tr>
              <td>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  毎日更新！
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blueText">
                  プレゼント
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  に応募できる
                </span>
              </td>
              <td>
                <span class="p-loginPanel_confirmBox_infoGroup_blueText">
                  クリップ機能
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  （お気に入り）が使える
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  「もうすぐ始まる／終わる 展覧会」が
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blueText">
                  メールで届く
                </span>
              </td>
              <td>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  「
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blueText">
                  For You
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  」機能で、あなた好みの情報が見られる
                </span>
              </td>
            </tr>
            <tr>
              <td>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  「
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blueText">
                  マイ ミュージアム メモ
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  」で、観賞レポートも書ける
                </span>
              </td>
              <td>
                <span class="p-loginPanel_confirmBox_infoGroup_blueText">
                  学芸員募集
                </span>
                <span class="p-loginPanel_confirmBox_infoGroup_blackText">
                  の詳細が閲覧できる
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <template v-if="!isMobile">
      <div class="c-listAdvertise">
        <div
          v-if="pageData.topAdPc && pageData.topAdPc.body"
          class="c-listAdvertise_advertiseCms c-advertise"
          v-html="pageData.topAdPc.body"
        />
        <!--
        <div
          class="c-listAdvertise_advertise c-advertise"
        >
          <adsbygoogle
            ad-slot="7741488425"
            ad-format="auto"
          ></adsbygoogle>
        </div>
        <div
          class="c-listAdvertise_advertise c-advertise"
        >
          <adsbygoogle
            ad-slot="7612157228"
            ad-format="auto"
          ></adsbygoogle>
        </div>
        -->
      </div>
    </template>

    <!--
    <template
      v-if="$device.isMobile"
    >
      <div
        class="c-listAdvertise"
      >
        <div
          class="c-listAdvertise_advertise c-advertise"
        >
          <adsbygoogle
            ad-slot="5687953794"
            ad-format="auto"
          ></adsbygoogle>
        </div>
      </div>
    </template>
    -->

    <v-overlay
      v-bind:model-value="storeUser.state.loading"
      scrim="#fff"
      opacity="0.7"
    >
      <v-progress-circular
        indeterminate
        color="#000"
        size="48"
      />
    </v-overlay>
  </div>
</template>

<style lang="scss" scoped>
@use '@/assets/css/foundation/mixin/responsive' as *;

.p-loginPanel {
  display: flex;
  position: relative;
  flex-direction: column;
  align-items: center;
  padding: 65px 0 80px;

  @include mq_down(sp) {
    padding-top: 40px;
    padding-bottom: 20px;
  }

  &_inner {
    width: 410px;

    @include mq_down(sp) {
      width: 100%;
    }
  }

  &_title {
    color: var(--color_text_base);
    font-size: 20px;
    font-weight: bold;

    @include mq_down(sp) {
      font-size: 18px;
      text-align: center;
    }
  }

  &_error {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
  }

  &_inputBlock {
    margin-top: 0;

    &_labelRow {
      display: flex;
      align-items: center;
      margin-bottom: 10px;

      &_text {
        font-size: 15px;
        font-weight: bold;
      }

      &_required {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 46px;
        height: 19px;
        margin-left: 9px;
        border-radius: 10px;
        background-color: var(--color_primary);
        color: var(--color_text_white);
        font-size: 13px;
      }
    }

    &_input {
      width: 410px;

      @include mq_down(sp) {
        width: 100%;
      }
    }
  }

  &_action {
    display: flex;
    justify-content: center;

    .c-btn_formSubmit_margin {
      margin-top: 42px;

      @include mq_down(sp) {
        margin-top: 40px;
      }
    }
  }

  &_forgotLink {
    display: flex;
    justify-content: center;
    margin-top: 15px;
    color: var(--color_primary);
    font-size: 14px;
  }

  &_groupLink {
    width: 100%;
    margin-top: 30px;
    background-color: #f8fafb;

    &_anotherLogin {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      margin: 25px 0 40px;

      @include mq_down(sp) {
        margin: 0;
        padding: 20px;
      }

      &_title {
        margin-bottom: 5px;
        color: var(--color_button_text_black);
        font-size: 20px;
        font-weight: bold;

        @include mq_down(sp) {
          font-size: 18px;
        }
      }

      &_linkButton {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 278px;
        height: 40px;
        margin-top: 15px;
        margin-left: -10px;
        background-color: var(--g_color_background_white);
        color: var(--color_text_base);
        font-size: 14px;

        &:hover {
          opacity: 0.7;
        }

        @include mq_down(sp) {
          width: 100%;
          max-width: 300px;
          margin: 6px 0;
        }

        &_iconFaceBook {
          width: 9px;
          height: 16px;
          margin-right: 20px;
        }

        &_iconTwitter {
          width: 16px;
          height: 14px;
          margin-right: 10px;
        }

        &_iconLine {
          width: 18px;
          height: 18px;
          margin-right: 10px;
        }
      }
    }
  }

  &_confirmBox {
    display: flex;
    flex-direction: column;
    align-items: center;
    width: 792px;
    margin-top: 26px;
    border: 4px solid var(--color_background_whiteblue);

    @include mq_down(sp) {
      display: block;
      box-sizing: border-box;
      width: 100%;
      max-width: 400px;
      margin: 25px auto 45px;
    }

    &_title {
      margin-top: 15px;
      margin-bottom: 10px;
      color: var(--color_primary);
      font-size: 18px;
      font-weight: bold;

      @include mq_down(sp) {
        margin: 0;
        padding: 20px;
        font-size: var(--fontSize_h2_sp);
      }
    }

    &_divider {
      width: 670px;
      height: 1px;

      @include mq_down(sp) {
        width: 100%;
      }
    }

    &_infoGroup {
      margin-top: 15px;
      margin-bottom: 25px;

      @include mq_down(sp) {
        margin: 0;
        padding: 20px;
      }

      td {
        padding: 0 25px;
      }

      @include mq_down(sp) {
        &>table,
        &>table>tbody,
        &>table>tbody>tr,
        &>table>tbody>tr>td {
          display: block;
        }

        &>table>tbody>tr>td {
          padding: 0;
          padding-left: 0.5em;
          text-indent: -0.5em;
        }
      }

      &_blueText {
        color: var(--color_primary);
        font-size: 14px;
        font-weight: bold;
      }

      &_blackText {
        color: var(--color_text_base);
        font-size: 14px;
        font-weight: bold;
      }
    }
  }
}

.foryou {
  .l-container_form {
    margin-top: 92px;
  }
}

.c-listAdvertise {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  width: 100%;

  &.is-onlyPC {
    @include mq_down(sp) {
      display: none;
    }
  }

  &.is-onlySP {
    display: none;

    @include mq_down(sp) {
      display: flex;
    }
  }

  &_advertise {
    width: 334px;

    // height: 280px;
    margin: 40px 15px 30px;

    @include mq_down(sp) {
      width: 100%;
      max-width: 334px;
      margin: 40px 0 0;
    }
  }

  &_advertiseCms {
    width: 334px;
    height: auto;
    margin: 60px 15px 30px;

    @include mq_down(sp) {
      width: 100%;
      max-width: 334px;
      height: auto;
      margin: 30px 0 0;
    }
  }
}
</style>
